import { useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import FileCustomSteps from "../../components/filePacket/FileCustomSteps";
import FileLifecycle from "../../components/filePacket/FileLifecycle";
import FilePdfButton from "../../components/filePacket/FilePdfButton";
import { AppLayout } from "../../components/layout/Header";
import CloseFileDialog from "../../components/messaging/CloseFileDialog";
import ExportButton from "../../components/messaging/ExportButton";
import { api } from "../../js/api/messaging";
import { FILE_SECTIONS, FILE_STATUSES, saveOverlay, STEP_STATUSES } from "../../js/api/filePacket";
import { addClientDocument } from "../../js/db/documents";
import { useDesk } from "../../js/hooks/useDesk";
import { useFilePacket } from "../../js/hooks/useFilePacket";
import { prettyValue, statusLabel } from "../../js/utils/formatters";
import { messagesPath } from "../../js/utils/messagingPaths";

function SectionNav({ section, onChange }) {
  return (
    <nav className="file-nav" aria-label="File sections">
      <ol>
        {FILE_SECTIONS.map((item) => (
          <li key={item.id}>
            <button
              type="button"
              className={`file-nav__link${section === item.id ? " is-active" : ""}`}
              onClick={() => onChange(item.id)}
            >
              {item.label}
            </button>
          </li>
        ))}
      </ol>
    </nav>
  );
}

function ProfileSection({ file, onChange, desk, session }) {
  const [notes, setNotes] = useState(file.overlay.profileNotes || "");

  function saveStatus(status) {
    saveOverlay(file.id, { status }, session, "file.status", { to: status });
    onChange();
  }

  function saveNotes(event) {
    event.preventDefault();
    saveOverlay(file.id, { profileNotes: notes }, session, "file.profile_notes");
    onChange();
  }

  return (
    <section className="card stack file-section" id="file-profile">
      <div className="page-header" style={{ marginBottom: 0 }}>
        <div>
          <h2>Client profile</h2>
          <p className="muted">Pulled from the existing {file.fileType} record and messaging file.</p>
        </div>
        <span className="badge">{prettyValue(file.fileType)}</span>
      </div>
      <dl className="file-meta">
        <div>
          <dt>Name</dt>
          <dd>{file.clientProfile.name || "—"}</dd>
        </div>
        <div>
          <dt>Email</dt>
          <dd>{file.clientProfile.email || "—"}</dd>
        </div>
        <div>
          <dt>Phone</dt>
          <dd>{file.clientProfile.phone || "—"}</dd>
        </div>
        <div>
          <dt>Address</dt>
          <dd>{file.clientProfile.address || file.propertyAddress || "—"}</dd>
        </div>
        <div>
          <dt>Agent</dt>
          <dd>{file.clientProfile.agent || "—"}</dd>
        </div>
        <div>
          <dt>Broker</dt>
          <dd>{file.clientProfile.broker || "—"}</dd>
        </div>
      </dl>
      <label className="field">
        <span className="field-label">File status</span>
        <select className="field-select" value={file.status} onChange={(event) => saveStatus(event.target.value)}>
          {FILE_STATUSES.map((item) => (
            <option key={item.id} value={item.id}>
              {item.label}
            </option>
          ))}
        </select>
      </label>
      <form className="stack" onSubmit={saveNotes}>
        <label className="field">
          <span className="field-label">Office notes on this File</span>
          <textarea
            className="field-textarea"
            rows={4}
            value={notes}
            onChange={(event) => setNotes(event.target.value)}
          />
        </label>
        <button type="submit" className="ghost-btn">
          Save file notes
        </button>
      </form>
      {file.specialNotes ? (
        <div>
          <h3>Notes from the {file.fileType === "seller" ? "seller portfolio" : "buyer checklist"}</h3>
          <p style={{ whiteSpace: "pre-wrap" }}>{file.specialNotes}</p>
        </div>
      ) : null}
      <p className="row" style={{ gap: "0.65rem", flexWrap: "wrap" }}>
        {file.fileType === "buyer" && file.clientProfile.id ? (
          <Link to={desk.client(file.clientProfile.id)} className="ghost-btn">
            Client Manager detail
          </Link>
        ) : null}
        <Link to={desk.files} className="ghost-btn">
          All files
        </Link>
      </p>
    </section>
  );
}

function ChecklistSection({ file }) {
  const checklist = file.fileType === "seller" ? file.sellerChecklist : file.buyerChecklist;
  return (
    <section className="card stack file-section" id="file-checklist">
      <div>
        <h2>{file.fileType === "seller" ? "Seller checklist" : "Buyer checklist"}</h2>
        <p className="muted">
          Live pages from the existing {file.fileType} journey
          {checklist?.progress ? ` · ${checklist.progress.percent}% complete` : ""}. This File does not
          keep a second copy.
        </p>
      </div>
      {(file.checklistSections || []).length ? (
        file.checklistSections.map((section) => (
          <article key={section.title}>
            <h3>{section.title}</h3>
            <ul>
              {section.lines.map((line) => (
                <li key={line}>{line}</li>
              ))}
            </ul>
          </article>
        ))
      ) : (
        <p className="muted">No checklist answers are saved yet.</p>
      )}
      {file.pageNotes?.length ? (
        <div>
          <h3>Page notes</h3>
          <ul>
            {file.pageNotes.map((note) => (
              <li key={note.page}>
                <strong>{note.page}:</strong> {note.text}
              </li>
            ))}
          </ul>
        </div>
      ) : null}
      <div>
        <h3>Tips</h3>
        <ul>
          {(file.tips || []).map((tip) => (
            <li key={tip.title}>
              {tip.title} — {tip.status}
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

function MilestoneList({ file, onChange, session }) {
  const items = file.transactionTimeline || [];
  if (file.fileType === "buyer") {
    return (
      <section className="card stack file-section">
        <div>
          <h2>Buyer transaction timeline</h2>
          <p className="muted">The existing nine Handy Transaction Checklist items. The client updates these.</p>
        </div>
        <ol className="file-milestone-list">
          {items.map((item) => (
            <li key={item.id}>
              <strong>{item.title}</strong>
              <span className="muted">{item.status ? prettyValue(item.status) : "Not started"}</span>
              <p className="muted">{item.detail}</p>
            </li>
          ))}
        </ol>
      </section>
    );
  }

  function setStatus(id, status) {
    saveOverlay(
      file.id,
      {
        sellerMilestones: {
          statuses: { ...(file.overlay.sellerMilestones?.statuses || {}), [id]: status },
          notes: file.overlay.sellerMilestones?.notes || "",
        },
      },
      session,
      "file.seller_milestone",
      { step: id }
    );
    onChange();
  }

  function setNotes(notes) {
    saveOverlay(
      file.id,
      {
        sellerMilestones: {
          statuses: file.overlay.sellerMilestones?.statuses || {},
          notes,
        },
      },
      session,
      "file.seller_milestone"
    );
    onChange();
  }

  return (
    <section className="card stack file-section">
      <div>
        <h2>Seller transaction timeline</h2>
        <p className="muted">
          Office equivalents of the buyer milestones. Listing readiness stays on the seller journey;
          this tracker lives on the File.
        </p>
      </div>
      <ol className="file-milestone-list">
        {items.map((item) => (
          <li key={item.id}>
            <strong>{item.title}</strong>
            <p className="muted">{item.detail}</p>
            <select value={item.status || "not_started"} onChange={(event) => setStatus(item.id, event.target.value)}>
              {STEP_STATUSES.map((option) => (
                <option key={option.id} value={option.id}>
                  {option.label}
                </option>
              ))}
            </select>
          </li>
        ))}
      </ol>
      <label className="field">
        <span className="field-label">Seller timeline notes</span>
        <textarea
          className="field-textarea"
          rows={3}
          defaultValue={file.sellerMilestoneNotes}
          onBlur={(event) => setNotes(event.target.value)}
        />
      </label>
    </section>
  );
}

export default function AgentFileDetail() {
  const { id } = useParams();
  const desk = useDesk();
  const { file, reload, session } = useFilePacket(id);
  const [params, setParams] = useSearchParams();
  const section = FILE_SECTIONS.some((item) => item.id === params.get("section"))
    ? params.get("section")
    : "profile";
  const [closeOpen, setCloseOpen] = useState(false);
  const [closeBusy, setCloseBusy] = useState(false);
  const [notice, setNotice] = useState("");
  const deskRole = desk.variant === "broker" ? "broker" : "agent";
  const messagesHref = file?.transactionId
    ? messagesPath(deskRole, { transactionId: file.transactionId })
    : desk.messages;

  function setSection(next) {
    const copy = new URLSearchParams(params);
    copy.set("section", next);
    setParams(copy, { replace: true });
  }

  async function attachToFile(event) {
    const upload = event.target.files?.[0];
    event.target.value = "";
    if (!upload || !file?.clientProfile?.id) return;
    try {
      const doc = await addClientDocument({
        ownerId: file.clientProfile.id,
        role: file.fileType,
        file: upload,
      });
      saveOverlay(
        file.id,
        {
          attachments: [
            ...(file.overlay.attachments || []),
            { id: doc.id, file_name: doc.file_name, mime_type: doc.mime_type, added_at: doc.created_at },
          ],
        },
        session,
        "file.attachment"
      );
      reload();
    } catch (err) {
      setNotice(err.message || "That file could not be attached.");
    }
  }

  async function closeFile(status) {
    if (!file?.transactionId) return;
    setCloseBusy(true);
    const result = await api("POST", `/api/transactions/${file.transactionId}/archive`, { status }, session);
    setCloseBusy(false);
    setCloseOpen(false);
    if (!result.ok) {
      setNotice(result.error || "The file could not be archived.");
      return;
    }
    saveOverlay(
      file.id,
      { status: status === "abandoned" ? "dissolved" : "closed" },
      session,
      "file.status",
      { to: status }
    );
    setNotice("The communication report was saved and this File moved to the archive.");
    reload();
  }

  if (!file) {
    return (
      <AppLayout variant={desk.variant === "broker" ? "broker" : "agent"}>
        <p>
          <Link to={desk.files}>← All files</Link>
        </p>
        <p>This File is not on this desk, or the client record is missing.</p>
      </AppLayout>
    );
  }

  return (
    <AppLayout variant={desk.variant === "broker" ? "broker" : "agent"}>
      <div className="file-workspace">
        <SectionNav section={section} onChange={setSection} />
        <aside className="file-rail">
          <FileCustomSteps file={file} onChange={reload} compact />
          <div className="file-rail-block">
            <h3>Timeline</h3>
            {file.timeline.length ? (
              <ul className="file-mini-list">
                {file.timeline.slice(0, 8).map((item, index) => (
                  <li key={`${item.at}-${index}`}>
                    <strong>{item.title}</strong>
                    <span className="muted">{item.stamp || prettyValue(item.source)}</span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="muted">Actions appear here as the File moves.</p>
            )}
          </div>
          <div className="file-rail-block">
            <h3>Attachments</h3>
            {file.attachments.length ? (
              <ul className="file-mini-list">
                {file.attachments.slice(0, 8).map((item) => (
                  <li key={item.id}>
                    {item.name}
                    <span className="muted">{prettyValue(item.source)}</span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="muted">Message files and uploads collect here.</p>
            )}
            <label className="file-upload">
              <span className="field-label">Add to this File</span>
              <input type="file" onChange={attachToFile} />
            </label>
          </div>
        </aside>
        <div className="file-main">
          <div className="page-header file-heading">
            <div>
              <p>
                <Link to={desk.files}>← All files</Link>
              </p>
              <h1>{file.clientProfile.name || "Client file"}</h1>
              <p className="page-lead">
                {file.statusLabel}
                {file.transactionStatus ? ` · messaging ${statusLabel(file.transactionStatus)}` : ""}
                {file.propertyAddress ? ` · ${file.propertyAddress}` : ""}
              </p>
            </div>
            <div className="file-heading__actions">
              <FilePdfButton file={file} onSaved={reload} className="primary-btn" />
              {file.transaction ? <ExportButton transaction={file.transaction} onNotice={setNotice} /> : null}
              {file.transaction && file.transaction.status === "open" ? (
                <button type="button" className="ghost-btn" onClick={() => setCloseOpen(true)}>
                  Close / archive
                </button>
              ) : null}
            </div>
          </div>
          {notice ? <p className="muted">{notice}</p> : null}

          {section === "profile" ? (
            <ProfileSection key={file.id} file={file} onChange={reload} desk={desk} session={session} />
          ) : null}
          {section === "checklist" ? <ChecklistSection file={file} /> : null}
          {section === "timeline" ? (
            <>
              <FileLifecycle file={file} onChange={reload} />
              <MilestoneList file={file} onChange={reload} session={session} />
              <FileCustomSteps key={`${file.id}-steps`} file={file} onChange={reload} />
              <section className="card stack file-section">
                <h2>Unified timeline</h2>
                <p className="muted">Messages, calendar, custom steps, lifecycle, and attachments in one record.</p>
                {file.timeline.length ? (
                  <ol className="file-action-timeline">
                    {file.timeline.map((item, index) => (
                      <li key={`${item.at}-${item.title}-${index}`}>
                        <header>
                          <strong>{item.title}</strong>
                          <time>{item.stamp}</time>
                        </header>
                        <p className="muted">
                          {prettyValue(item.source)}
                          {item.text ? ` · ${item.text}` : ""}
                        </p>
                      </li>
                    ))}
                  </ol>
                ) : (
                  <p className="muted">Nothing has been recorded on this File yet.</p>
                )}
              </section>
            </>
          ) : null}
          {section === "messages" ? (
            <section className="card stack file-section">
              <div>
                <h2>Messaging summary</h2>
                <p className="muted">
                  Full compose and threads stay in Messages. This File shows the same conversation.
                </p>
              </div>
              {file.messages.length ? (
                <ol className="file-action-timeline">
                  {file.messages.map((item, index) => (
                    <li key={`${item.at}-${index}`}>
                      <header>
                        <strong>{item.title}</strong>
                        <time>{item.stamp}</time>
                      </header>
                      <p style={{ whiteSpace: "pre-wrap" }}>{item.text}</p>
                    </li>
                  ))}
                </ol>
              ) : (
                <p className="muted">No messages on this File yet.</p>
              )}
              <Link to={messagesHref} className="primary-btn">
                Open in Messages
              </Link>
            </section>
          ) : null}
          {section === "calendar" ? (
            <section className="card stack file-section">
              <div>
                <h2>Calendar summary</h2>
                <p className="muted">Appointments already on the desk calendar that match this client.</p>
              </div>
              {file.calendarEvents.length ? (
                <ul className="stack" style={{ listStyle: "none", padding: 0 }}>
                  {file.calendarEvents.map((event) => (
                    <li key={event.id}>
                      <strong>{event.title}</strong>
                      <div className="muted">
                        {[event.date, event.time, prettyValue(event.type), event.location].filter(Boolean).join(" · ")}
                      </div>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="muted">No calendar events are tied to this client yet.</p>
              )}
              <Link to={desk.calendar} className="primary-btn">
                Open Calendar
              </Link>
            </section>
          ) : null}
          {section === "pdf" ? (
            <section className="card stack file-section">
              <div>
                <h2>File Packet PDF</h2>
                <p className="muted">
                  One packet: profile, checklist pages, notes and tips, transaction timeline, custom
                  steps, messages, calendar, attachments, lifecycle, and final status. Uses the
                  existing jsPDF report engine.
                </p>
              </div>
              <FilePdfButton file={file} onSaved={reload} />
              {file.pdfPacket?.generatedAt ? (
                <p className="muted">
                  Last generated {file.pdfPacket.generatedAt}
                  {file.pdfPacket.filename ? ` · ${file.pdfPacket.filename}` : ""}
                </p>
              ) : (
                <p className="muted">No packet has been generated yet.</p>
              )}
              {file.transaction ? (
                <p className="muted">
                  “Save report” on this File still writes the communication record used by Close /
                  Archive. The File Packet is the full office export.
                </p>
              ) : null}
            </section>
          ) : null}
        </div>
      </div>
      <CloseFileDialog
        transaction={file.transaction}
        open={closeOpen}
        busy={closeBusy}
        onCancel={() => setCloseOpen(false)}
        onConfirm={closeFile}
      />
    </AppLayout>
  );
}
